import { useState, useEffect, useMemo } from 'react'
import { Outlet, NavLink, useLocation, Link } from 'react-router-dom'
import { useAdminAuth } from './AdminGuard'
import { ADMIN_ROLES } from '../lib/permissions'

// ── Nav item definition ──────────────────────────────────────────────────────
interface NavItem {
  label: string
  path: string
  icon: React.ReactNode
  exactMatch?: boolean
  /** Required permission to see this nav item. '*' = super_admin only */
  requiredPermission?: string
}

function DashboardIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <rect x="3" y="3" width="8" height="8" rx="2" />
      <rect x="13" y="3" width="8" height="8" rx="2" />
      <rect x="3" y="13" width="8" height="8" rx="2" />
      <rect x="13" y="13" width="8" height="8" rx="2" />
    </svg>
  )
}

function UsersIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  )
}

function ModerationIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
    </svg>
  )
}

function ContentIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zm-5 8h8v-2H8v2zm0-4h8v-2H8v2zm0-4h5V7H8v2z" />
    </svg>
  )
}

function SubscriptionsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
    </svg>
  )
}

function NotificationsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
    </svg>
  )
}

function MonitoringIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3.5 18.5l4-8 4 3 4-6.5 4 11.5H3.5zm0 0" opacity=".3"/>
      <path d="M22 20H2V4h2v14h2l3.5-7 4 3 3.5-5.5L20 20h2v-4l-3-6-3.5 5.5-4-3L8 20H6V6H4V2H2v20h20v-2z"/>
    </svg>
  )
}

function AuditIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  )
}


function DesignSystemIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r="2.5" />
      <circle cx="19" cy="13" r="2.5" />
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="10" cy="20" r="2.5" />
      <path d="M2 12h2M7 18l-2.5 2M13.5 4v-2M18.5 11l2-2" />
    </svg>
  )
}

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
    </svg>
  )
}

function HamburgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
    </svg>
  )
}

const NAV_ITEMS: NavItem[] = [
  { label: '대시보드', path: '/', icon: <DashboardIcon />, exactMatch: true, requiredPermission: 'dashboard.view' },
  { label: '사용자 관리', path: '/users', icon: <UsersIcon />, requiredPermission: 'users.view' },
  { label: '콘텐츠 모더레이션', path: '/moderation', icon: <ModerationIcon />, requiredPermission: 'moderation.view' },
  { label: '프리셋 콘텐츠', path: '/content', icon: <ContentIcon />, requiredPermission: 'content.view' },
  { label: '구독 관리', path: '/subscriptions', icon: <SubscriptionsIcon />, requiredPermission: 'subscriptions.view' },
  { label: '알림 관리', path: '/notifications', icon: <NotificationsIcon />, requiredPermission: 'notifications.view' },
  { label: '건강 모니터링', path: '/monitoring', icon: <MonitoringIcon />, requiredPermission: 'monitoring.view' },
  { label: '감사 로그', path: '/audit', icon: <AuditIcon />, requiredPermission: 'audit.view' },
  { label: '인증 진단', path: '/auth-events', icon: <AuditIcon />, requiredPermission: 'auth_events.view' },
  { label: '신고 관리', path: '/reports', icon: <ModerationIcon />, requiredPermission: 'reports.view' },
  { label: '문의 관리', path: '/feedback', icon: <ContentIcon />, requiredPermission: 'feedback.view' },
  { label: '앱 설정', path: '/app-config', icon: <MonitoringIcon />, requiredPermission: 'app_config.view' },
  { label: '디자인 시스템', path: '/design-system', icon: <DesignSystemIcon />, requiredPermission: 'content.view' },
  { label: 'A/B 테스트', path: '/ab-test', icon: <ContentIcon />, requiredPermission: 'ab_test.view' },
]

const SIDEBAR_WIDTH = 260

// ── Sidebar content (shared between desktop + mobile drawer) ────────────────
interface SidebarContentProps {
  onNavClick?: () => void
}

function SidebarContent({ onNavClick }: SidebarContentProps) {
  const location = useLocation()
  const { logout, user, role, hasPermission } = useAdminAuth()

  // Filter nav items based on admin's permissions
  const visibleNavItems = useMemo(() => {
    return NAV_ITEMS.filter((item) => {
      if (!item.requiredPermission) return true
      return hasPermission(item.requiredPermission)
    })
  }, [hasPermission])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '0',
      }}
    >
      {/* Logo */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '24px 20px',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <img src="/logo.png" alt="심장길" style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0 }} />
        <div>
          <p
            style={{
              fontSize: '16px',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              lineHeight: 1.2,
            }}
          >
            심장길 Admin
          </p>
          <p style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', marginTop: '2px' }}>
            관리자 대시보드
          </p>
        </div>
      </div>

      {/* Nav items — filtered by current admin's permissions */}
      <nav style={{ flex: 1, padding: '12px 12px', overflowY: 'auto' }}>
        {visibleNavItems.map((item) => {
          const isActive = item.exactMatch
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path)

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onNavClick}
              end={item.exactMatch}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '11px 14px',
                marginBottom: '2px',
                borderRadius: '10px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: isActive ? 600 : 500,
                color: isActive ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                background: isActive ? 'var(--color-primary-light)' : 'transparent',
                borderLeft: isActive ? '3px solid var(--color-primary)' : '3px solid transparent',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              <span
                style={{
                  opacity: isActive ? 1 : 0.7,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          )
        })}
      </nav>

      {/* Bottom actions */}
      <div
        style={{
          padding: '12px 12px 20px',
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <a
          href="https://heart-way.life"
          onClick={onNavClick}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '11px 14px',
            borderRadius: '10px',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => {
            ;(e.currentTarget as HTMLElement).style.background =
              'var(--color-surface-container-low)'
          }}
          onMouseLeave={(e) => {
            ;(e.currentTarget as HTMLElement).style.background = 'transparent'
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', opacity: 0.7 }}>
            <BackIcon />
          </span>
          메인 사이트로
        </a>

        {/* Admin info */}
        {user && (
          <div style={{
            padding: '10px 14px', borderRadius: '10px',
            background: 'var(--color-surface-container-low)',
            marginBottom: '4px',
          }}>
            <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.username}
            </p>
            <p style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
              {role ? (ADMIN_ROLES[role]?.label ?? role) : 'Admin'}
            </p>
          </div>
        )}

        <button
          onClick={() => {
            onNavClick?.()
            logout()
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '11px 14px',
            borderRadius: '10px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
            fontFamily: 'inherit',
            width: '100%',
            textAlign: 'left',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => {
            ;(e.currentTarget as HTMLElement).style.background =
              'var(--color-surface-container-low)'
          }}
          onMouseLeave={(e) => {
            ;(e.currentTarget as HTMLElement).style.background = 'transparent'
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', opacity: 0.7 }}>
            <LogoutIcon />
          </span>
          로그아웃
        </button>
      </div>
    </div>
  )
}

// ── Main layout ──────────────────────────────────────────────────────────────
export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768)
  const location = useLocation()

  // Track viewport width
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDesktop(e.matches)
      if (e.matches) setSidebarOpen(false)
    }
    mq.addEventListener('change', handleChange)
    return () => mq.removeEventListener('change', handleChange)
  }, [])

  // Close mobile sidebar on route change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSidebarOpen(false)
  }, [location.pathname])

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (!isDesktop && sidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [sidebarOpen, isDesktop])

  return (
    <div
      className="admin-root"
      style={{
        display: 'flex',
        height: '100dvh',
        background: 'var(--color-background)',
        fontFamily: "'Pretendard Variable', Pretendard, -apple-system, sans-serif",
      }}
    >
      {/* ── Desktop sidebar ────────────────────────────────────────────────── */}
      {isDesktop && (
        <aside
          style={{
            width: `${SIDEBAR_WIDTH}px`,
            flexShrink: 0,
            background: 'var(--color-surface)',
            borderRight: '1px solid var(--color-border)',
            height: '100dvh',
            position: 'sticky',
            top: 0,
            overflowY: 'auto',
          }}
        >
          <SidebarContent />
        </aside>
      )}

      {/* ── Mobile: overlay backdrop ────────────────────────────────────────── */}
      {!isDesktop && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.4)',
            zIndex: 40,
            animation: 'fadeIn 0.2s ease',
          }}
          aria-hidden="true"
        />
      )}

      {/* ── Mobile: slide-in drawer ─────────────────────────────────────────── */}
      {!isDesktop && (
        <aside
          aria-label="관리자 사이드바"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${SIDEBAR_WIDTH}px`,
            background: 'var(--color-surface)',
            borderRight: '1px solid var(--color-border)',
            zIndex: 50,
            overflowY: 'auto',
            transform: sidebarOpen ? 'translateX(0)' : `translateX(-${SIDEBAR_WIDTH}px)`,
            transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: sidebarOpen ? 'var(--shadow-lg)' : 'none',
          }}
        >
          {/* Close button inside drawer */}
          <button
            onClick={() => setSidebarOpen(false)}
            aria-label="메뉴 닫기"
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: 'none',
              background: 'var(--color-surface-container-low)',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </button>

          <SidebarContent onNavClick={() => setSidebarOpen(false)} />
        </aside>
      )}

      {/* ── Main area ──────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, height: '100%' }}>
        {/* Mobile top header */}
        {!isDesktop && (
          <header
            style={{
              height: '56px',
              flexShrink: 0,
              background: 'var(--color-surface)',
              borderBottom: '1px solid var(--color-border)',
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
              gap: '12px',
              zIndex: 30,
            }}
          >
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="메뉴 열기"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                border: 'none',
                background: 'transparent',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HamburgerIcon />
            </button>

            {/* Page title (derived from current nav item) */}
            <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
              {NAV_ITEMS.find((item) =>
                item.exactMatch
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path)
              )?.label ?? '관리자'}
            </span>

            {/* Heart logo mark */}
            <div style={{ marginLeft: 'auto' }}>
              <img src="/logo.png" alt="심장길" style={{ width: '30px', height: '30px', borderRadius: '8px' }} />
            </div>
          </header>
        )}

        {/* Page content */}
        <main
          style={{
            flex: 1,
            minHeight: 0,
            padding: isDesktop ? '32px' : '20px 16px 32px',
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}
